import { useTranslation } from "react-i18next"
import { UseMutationConfig } from "react-relay"
import { Disposable, MutationParameters } from "relay-runtime"
import { toast } from "@/hooks/useToast"

type GenesysMutationConfig<TMutation extends MutationParameters> = Pick<
  UseMutationConfig<TMutation>,
  "variables" | "onCompleted"
> & {
  onAgentNotAuthenticated: VoidFunction
}

const useGenesysMutationErrorHandling = <TMutation extends MutationParameters, TErrorTypename extends string>(
  commit: (config: UseMutationConfig<TMutation>) => Disposable,
  selectErrors: (response: TMutation["response"]) => ReadonlyArray<{ __typename: TErrorTypename }> | null | undefined,
  errorMessageKeyByTypename: NoInfer<Partial<Record<TErrorTypename, string>>>,
) => {
  const { t } = useTranslation()

  const showGenericErrorToast = () => {
    toast({ description: t("error.500.description") })
  }

  return ({ variables, onCompleted, onAgentNotAuthenticated }: GenesysMutationConfig<TMutation>) => {
    commit({
      variables,
      onCompleted: (response, payloadErrors) => {
        if (payloadErrors?.length) {
          showGenericErrorToast()
          return
        }

        const firstError = selectErrors(response)?.[0]
        if (!firstError) {
          onCompleted?.(response, payloadErrors)
          return
        }

        if (firstError.__typename === "AgentNotAuthenticated") {
          onAgentNotAuthenticated()
          return
        }

        toast({ description: t(errorMessageKeyByTypename[firstError.__typename] ?? "error.500.description") })
      },
      onError: showGenericErrorToast,
    })
  }
}

export { useGenesysMutationErrorHandling }
